import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { fetchGql } from '@/lib/graphql_client';
import { GlobalSuggestDocument } from '@/gql/graphql';
import type { GlobalSuggestHit } from '@/gql/graphql';
import { shouldDropResponse } from './stale-response';

const DEBOUNCE_MS = 150;
const MIN_QUERY_LENGTH = 2;
const STALE_TIME_MS = 30_000;

export interface UseGlobalSearchResult {
  hits: GlobalSuggestHit[];
  isFetching: boolean;
  /** True while the first fetch is in-flight (hits === undefined) */
  isFirstFetch: boolean;
  partialFailure: boolean;
  echoQuery: string;
}

/**
 * TanStack Query hook for the globalSuggest endpoint.
 *
 * - Debounces the query by 150ms before firing (§6.1).
 * - Drops responses where echoQuery !== debouncedQuery (stale-response pattern, §6.1).
 *   The debouncedQuery is the exact string sent to the BE, so echoQuery must match it.
 * - Does not fire for queries shorter than 2 characters (handover §2).
 * - staleTime 30s — suggest queries are cheap; results stable for short periods.
 */
export function useGlobalSearch(
  query: string,
  enabled: boolean,
): UseGlobalSearchResult {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce: wait 150ms after last keystroke before updating debouncedQuery
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const shouldFetch =
    enabled && debouncedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, isPending } = useQuery({
    queryKey: ['globalSuggest', debouncedQuery],
    // queryFn receives an AbortSignal that fires when this query is superseded
    // (debouncedQuery changes) or when the consumer unmounts. Passing it through
    // to fetchGql causes the underlying fetch to abort — superseded in-flight
    // requests are cancelled at the network layer instead of completing then
    // being discarded in the stale-response check.
    queryFn: ({ signal }) =>
      fetchGql(
        GlobalSuggestDocument,
        { query: debouncedQuery, limit: 20 },
        signal,
      ).then((result) => result.globalSuggest),
    enabled: shouldFetch,
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  // Stale-response drop: echoQuery must match the debouncedQuery that triggered this fetch.
  // Guards against any out-of-order delivery the AbortSignal cancellation didn't catch
  // (e.g. response in flight when the new key took over the active subscription).
  const result = data && !shouldDropResponse(data.echoQuery, debouncedQuery)
    ? data
    : null;

  // Defensive de-dupe by composite hit `id` (`${tenant}:${kind}:${source_id}`).
  // The BE shouldn't return the same composite ID twice, but if it ever does
  // (projection drift, duplicate write race), don't render the same row twice.
  // Note: distinct entities with identical displayName but different source_ids
  // are NOT collapsed — they have different `id` values and are legitimately
  // different records (handover §1: `id` is the stable React key).
  const seenIds = new Set<string>();
  const hits = (result?.hits ?? []).filter((h) => {
    if (seenIds.has(h.id)) return false;
    seenIds.add(h.id);
    return true;
  });
  const partialFailure = result?.partialFailure ?? false;
  const echoQuery = result?.echoQuery ?? '';
  const isFirstFetch = isFetching && isPending;

  return {
    hits,
    isFetching,
    isFirstFetch,
    partialFailure,
    echoQuery,
  };
}
